import React, { useEffect, useState } from "react";

import { ordersService } from "api/services/orders.service";
import Button from "components/UI/Button";
import Modal from "components/UI/Modal";
import { getApiErrorMessage } from "utils/api-error";

import type { AdminOrderCancelResponse, AdminOrderCancelTarget } from "api/types/order.types";

import "./ProductOrderCancelModal.scss";

const MAX_REASON_LENGTH = 200;

const formatAmount = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return "-";
  return `${num.toLocaleString()}원`;
};

interface ProductOrderCancelModalProps {
  open: boolean;
  order: AdminOrderCancelTarget | null;
  onClose: () => void;
  /** 취소 성공 후 주문 정보를 갱신하기 위한 콜백 */
  onCancelled?: () => void;
}

const ProductOrderCancelModal: React.FC<ProductOrderCancelModalProps> = ({ open, order, onClose, onCancelled }) => {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<AdminOrderCancelResponse | null>(null);

  useEffect(() => {
    if (open) {
      setReason("");
      setError("");
      setResult(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!order) return;
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      setError("취소 사유를 입력해 주세요.");
      return;
    }
    if (trimmed.length > MAX_REASON_LENGTH) {
      setError(`취소 사유는 최대 ${MAX_REASON_LENGTH}자까지 입력할 수 있습니다.`);
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      const res = await ordersService.cancel(order.id, trimmed);
      setResult(res.data);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDone = () => {
    onCancelled?.();
    onClose();
  };

  const isTravel = order?.product_type === "TRAVEL";

  // 취소 완료 후 결과 화면
  if (result) {
    return (
      <Modal
        open={open}
        title="주문 취소 완료"
        width="480px"
        onClose={handleDone}
        closeOnOverlayClick={false}
        footer={
          <div className="product-cancel__footer">
            <Button variant="primary" onClick={handleDone}>
              확인
            </Button>
          </div>
        }
      >
        <div className="product-cancel">
          <p className="product-cancel__done-message">{result.message}</p>
          <dl className="product-cancel__result">
            <div className="product-cancel__result-row">
              <dt>결제 환불</dt>
              <dd>{result.refunded ? `환불 완료 (${formatAmount(result.cancel_amt)})` : "환불 없음 (결제 전 취소)"}</dd>
            </div>
            {result.stock_restored != null && (
              <div className="product-cancel__result-row">
                <dt>재고 복구</dt>
                <dd>{result.stock_restored}</dd>
              </div>
            )}
            {isTravel && result.departure_id != null && (
              <div className="product-cancel__result-row">
                <dt>투어 회차</dt>
                <dd>#{result.departure_id}</dd>
              </div>
            )}
            <div className="product-cancel__result-row">
              <dt>주문 상태</dt>
              <dd>
                {result.previous_status} → {result.current_status}
              </dd>
            </div>
            <div className="product-cancel__result-row">
              <dt>처리자</dt>
              <dd>{result.cancelled_by || "-"}</dd>
            </div>
            <div className="product-cancel__result-row">
              <dt>취소 사유</dt>
              <dd>{result.cancel_reason || "-"}</dd>
            </div>
          </dl>
        </div>
      </Modal>
    );
  }

  // 취소 사유 입력 화면
  return (
    <Modal
      open={open}
      title="주문 취소"
      width="480px"
      onClose={submitting ? undefined : onClose}
      closeOnOverlayClick={!submitting}
      footer={
        <div className="product-cancel__footer">
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            닫기
          </Button>
          <Button variant="danger" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "처리 중..." : "주문 취소"}
          </Button>
        </div>
      }
    >
      <div className="product-cancel">
        {error && <div className="product-cancel__error">{error}</div>}
        <p className="product-cancel__notice">{isTravel ? "주문을 취소하면 결제 금액이 PG를 통해 환불되고 투어 잔여 좌석이 복구됩니다. 취소 후에는 되돌릴 수 없습니다." : "주문을 취소하면 결제 금액이 PG를 통해 환불되고 상품 재고가 복구됩니다. 취소 후에는 되돌릴 수 없습니다."}</p>
        {order && (
          <dl className="product-cancel__summary">
            {order.order_number ? (
              <div className="product-cancel__summary-row">
                <dt>주문번호</dt>
                <dd>{order.order_number}</dd>
              </div>
            ) : (
              <div className="product-cancel__summary-row">
                <dt>주문 ID</dt>
                <dd>#{order.id}</dd>
              </div>
            )}
            <div className="product-cancel__summary-row">
              <dt>결제 금액</dt>
              <dd>{formatAmount(order.total_amount)}</dd>
            </div>
          </dl>
        )}
        <label className="product-cancel__label" htmlFor="product-cancel-reason">
          취소 사유
        </label>
        <textarea id="product-cancel-reason" className="product-cancel__textarea" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={MAX_REASON_LENGTH} rows={4} placeholder="취소 사유를 입력하세요 (최대 200자)" disabled={submitting} />
        <div className="product-cancel__count">
          {reason.length} / {MAX_REASON_LENGTH}
        </div>
      </div>
    </Modal>
  );
};

export default ProductOrderCancelModal;
